import { Heart, Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchSettings } from '../store/slices/settingsSlice';
import { AtootbandanLogo } from './AtootbandanLogo';

interface FooterProps {
  onAdminAccess?: () => void;
}

export function Footer({ onAdminAccess }: FooterProps = {}) {
  const dispatch = useAppDispatch();
  const { settings, loading } = useAppSelector((state) => state.settings);

  useEffect(() => {
    // Fetch settings on component mount
    // Will use cached data if available and not expired
    dispatch(fetchSettings());
  }, [dispatch]);

  // If no settings loaded from backend, show minimal footer
  if (!settings) {
    return (
      <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <AtootbandanLogo size="md" showText={true} className="text-white" />
            </div>
            <p className="text-gray-400 text-sm">
              © 2024 All rights reserved. Made with{' '}
              <Heart className="inline h-4 w-4 text-rose-500 fill-current" /> in India
            </p>
          </div>
        </div>
      </footer>
    );
  }

  // Get active links only - with proper null checks
  const activeQuickLinks = settings?.quickLinks?.filter(link => link.isActive) || [];
  const activeSupportLinks = settings?.supportLinks?.filter(link => link.isActive) || [];
  const activeSocialLinks = settings?.socialLinks?.filter(link => link.isActive) || [];

  // Map platform names to icons
  const getSocialIcon = (platform: string) => {
    switch (platform) {
      case 'facebook':
        return <Facebook className="h-5 w-5" />;
      case 'twitter':
        return <Twitter className="h-5 w-5" />;
      case 'instagram':
        return <Instagram className="h-5 w-5" />;
      case 'linkedin':
        return <Linkedin className="h-5 w-5" />;
      default:
        return null;
    }
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Newsletter Section */}
      <div className="border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold mb-2">Stay Updated</h3>
              <p className="text-gray-400">Get the latest success stories and tips delivered to your inbox</p>
            </div>
            <div className="flex w-full md:w-auto space-x-2">
              <Input
                type="email"
                placeholder="Enter your email"
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 w-full md:w-80"
              />
              <Button className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 border-0">
                Subscribe
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Column */}
          <div>
            <div className="mb-4">
              <AtootbandanLogo size="md" showText={true} />
            </div>
            <p className="text-gray-400 mb-6">
              {settings?.branding?.description || "India's most trusted matrimony service helping millions find their perfect life partner."}
            </p>
            <div className="flex space-x-3">
              {activeSocialLinks.map(link => (
                <a 
                  key={link.platform} 
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="ghost" size="icon" className="hover:bg-white/10 rounded-full">
                    {getSocialIcon(link.platform)}
                  </Button>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {activeQuickLinks.map(link => (
                <li key={link._id}>
                  <a href={link.url} className="text-gray-400 hover:text-white transition-colors">
                    {link.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-bold text-lg mb-4">Support</h4>
            <ul className="space-y-3">
              {activeSupportLinks.map(link => (
                <li key={link._id}>
                  <a href={link.url} className="text-gray-400 hover:text-white transition-colors">
                    {link.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-lg mb-4">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-400">
                  {settings?.contactInfo?.address || '123 Matrimony Street, Mumbai, Maharashtra 400001'}
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-rose-500 flex-shrink-0" />
                <span className="text-gray-400">{settings?.contactInfo?.phone || '+91 1800-123-4567'}</span>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-rose-500 flex-shrink-0" />
                <span className="text-gray-400">{settings?.contactInfo?.email || 'support@soulmate.com'}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © 2024 {settings?.branding?.name || 'Atootbandan'}. All rights reserved. Made with{' '}
              <Heart className="inline h-4 w-4 text-rose-500 fill-current" /> in India
            </p>
            <div className="flex space-x-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                Privacy
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                Terms
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                Cookies
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}